/**
 * Nut Sheet Page
 *
 * Daily New + Used vehicle transaction sheet for each rooftop. Admin/CEO/
 * Executive only in V1. Three responsibilities on this page:
 *   1. Pick a (location, date) to view.
 *   2. Edit transaction rows for that day. Stock-# field auto-fills metadata
 *      from DealerBuilt; the rest is operator-entered.
 *   3. MTD summary cards (New / Used / Value Center / Totals / Wholesale +
 *      Adjustments) for the same calendar month. Wholesale (USED type "W") and
 *      Value Center (USED type ending in "VC") are each split out of Used and
 *      excluded from Totals; the Value Center card only appears when that month
 *      has VC sales.
 *
 * Concurrent editing: rows auto-refresh on a 5s visible-tab poll and each save
 * uses optimistic concurrency (a `version` + conditional write). Two people on
 * different rows never collide; a same-row conflict surfaces an inline banner
 * instead of silently overwriting (see mergeRows + handleResolveConflict).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { canAccessNutSheet } from '../auth/permissions';
import { NutSheetHeader } from '../components/nutSheet/NutSheetHeader';
import { NutSheetTable } from '../components/nutSheet/NutSheetTable';
import { dealAutofillPatch } from '../components/nutSheet/NutSheetRow';
import { DuplicateDealModal } from '../components/nutSheet/DuplicateDealModal';
import { RowHistoryModal } from '../components/nutSheet/RowHistoryModal';
import { NutSheetExportModal } from '../components/nutSheet/NutSheetExportModal';
import { NutSheetPrintModal } from '../components/nutSheet/NutSheetPrintModal';
import {
  NewVehicleSummary,
  UsedVehicleSummary,
  ValueCenterVehicleSummary,
  WholesaleVehicleSummary,
  TotalsSummary,
} from '../components/nutSheet/SummaryTables';
import {
  deleteNutSheetEntry,
  listNutSheetEntries,
  saveNutSheetEntry,
} from '../services/nutSheetApi';
import {
  DealLookupResponse,
  ExistingNutSheetMatch,
  MtdSummary,
  NutSheetEntry,
  NutSheetRowDraft,
  SaveEntryRequest,
} from '../types/nutSheet';

// localStorage keys for the table row-visibility toggle preferences.
const SHOW_WHOLESALE_KEY = 'nutSheet.showWholesale';
const SHOW_ONLY_WHOLESALE_KEY = 'nutSheet.showOnlyWholesale';
const SHOW_ONLY_VALUE_CENTER_KEY = 'nutSheet.showOnlyValueCenter';
const EXCLUDE_VALUE_CENTER_KEY = 'nutSheet.excludeValueCenter';
const SHOW_SWAP_KEY = 'nutSheet.showSwap';
const EXCLUDE_FI_RESERVE_KEY = 'nutSheet.excludeFiReserve';

/**
 * A boolean flag persisted per-browser in localStorage. Returns the current
 * value and a toggle that flips + persists it. Storage failures (private mode)
 * degrade gracefully to in-memory only.
 */
function usePersistedFlag(storageKey: string, defaultValue: boolean): [boolean, () => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored == null ? defaultValue : stored !== 'false';
    } catch {
      return defaultValue;
    }
  });
  const toggle = () => {
    setValue((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey, String(next));
      } catch {
        /* storage disabled — in-memory only */
      }
      return next;
    });
  };
  return [value, toggle];
}

const EMPTY_SUMMARY: MtdSummary = {
  byType: [],
  totals: { units: 0, gross: 0, doc: 0, holdback: 0, fAndI: 0 },
  adjustments: 0,
  wholesale: {
    byType: [],
    totals: { units: 0, gross: 0, doc: 0, holdback: 0, fAndI: 0 },
    adjustments: 0,
  },
  valueCenter: {
    byType: [],
    totals: { units: 0, gross: 0, doc: 0, holdback: 0, fAndI: 0 },
    adjustments: 0,
  },
};

function newClientId(): string {
  return `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

function entryToDraft(e: NutSheetEntry, existingClientId?: string): NutSheetRowDraft {
  return {
    entryId: e.entryId,
    version: e.version ?? null,
    // Reuse the caller's clientId when refreshing a row in place so React keeps
    // the same element (no remount / blur-thrash); mint a new one otherwise.
    clientId: existingClientId ?? newClientId(),
    locationId: e.locationId,
    date: e.date,
    type: e.type,
    category: e.category,
    stockNumber: e.stockNumber,
    dealNumber: e.dealNumber ?? null, // legacy rows lack the attribute
    soldYear: e.soldYear,
    soldMake: e.soldMake,
    soldModel: e.soldModel,
    tradeStockNumber: e.tradeStockNumber,
    tradeYear: e.tradeYear,
    tradeMake: e.tradeMake,
    tradeModel: e.tradeModel,
    tradeMiles: e.tradeMiles,
    tradeComment: e.tradeComment,
    trade2StockNumber: e.trade2StockNumber,
    trade2Year: e.trade2Year,
    trade2Make: e.trade2Make,
    trade2Model: e.trade2Model,
    trade2Miles: e.trade2Miles,
    trade2Comment: e.trade2Comment,
    acv: e.acv,
    retail: e.retail,
    t1Plan: e.t1Plan,
    trade2Acv: e.trade2Acv,
    trade2Retail: e.trade2Retail,
    t2Plan: e.t2Plan,
    daysInStock: e.daysInStock,
    estimatedGross: e.estimatedGross,
    actualGross: e.actualGross,
    fAndI: e.fAndI,
    points: e.points,
    holdback: e.holdback,
    doc: e.doc,
    financeSource: e.financeSource,
    financeSourceName: e.financeSourceName,
    financeReserve: e.financeReserve,
    openROs: e.openROs ?? null,
    salesperson: e.salesperson,
    salesperson2: e.salesperson2,
    salesManager: e.salesManager,
    fiRep: e.fiRep,
    customerLastName: e.customerLastName,
    csiBonus: e.csiBonus,
    accountingComment: e.accountingComment ?? null, // legacy rows lack the attribute
    comment: e.comment ?? null, // legacy rows lack the attribute
    isDirty: false,
  };
}

function buildBlankDraft(locationId: number, date: string): NutSheetRowDraft {
  return {
    entryId: null,
    version: null,
    clientId: newClientId(),
    locationId,
    date,
    type: '',
    category: 'NEW',
    stockNumber: '',
    dealNumber: null,
    soldYear: null,
    soldMake: null,
    soldModel: null,
    tradeStockNumber: null,
    tradeYear: null,
    tradeMake: null,
    tradeModel: null,
    tradeMiles: null,
    tradeComment: null,
    trade2StockNumber: null,
    trade2Year: null,
    trade2Make: null,
    trade2Model: null,
    trade2Miles: null,
    trade2Comment: null,
    acv: null,
    retail: null,
    t1Plan: null,
    trade2Acv: null,
    trade2Retail: null,
    t2Plan: null,
    daysInStock: null,
    estimatedGross: null,
    actualGross: null,
    fAndI: null,
    points: null,
    holdback: null,
    doc: null,
    financeSource: null,
    financeSourceName: null,
    financeReserve: null,
    openROs: null,
    salesperson: null,
    salesperson2: null,
    salesManager: null,
    fiRep: null,
    customerLastName: null,
    csiBonus: null,
    accountingComment: null,
    comment: null,
    isDirty: false,
  };
}

function todayInEastern(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function draftToRequest(d: NutSheetRowDraft): SaveEntryRequest {
  return {
    entryId: d.entryId ?? undefined,
    expectedVersion: d.version ?? undefined,
    locationId: d.locationId,
    date: d.date,
    type: d.type,
    category: d.category,
    stockNumber: d.stockNumber,
    dealNumber: d.dealNumber,
    soldYear: d.soldYear,
    soldMake: d.soldMake,
    soldModel: d.soldModel,
    tradeStockNumber: d.tradeStockNumber,
    tradeYear: d.tradeYear,
    tradeMake: d.tradeMake,
    tradeModel: d.tradeModel,
    tradeMiles: d.tradeMiles,
    tradeComment: d.tradeComment,
    trade2StockNumber: d.trade2StockNumber,
    trade2Year: d.trade2Year,
    trade2Make: d.trade2Make,
    trade2Model: d.trade2Model,
    trade2Miles: d.trade2Miles,
    trade2Comment: d.trade2Comment,
    acv: d.acv,
    retail: d.retail,
    t1Plan: d.t1Plan,
    trade2Acv: d.trade2Acv,
    trade2Retail: d.trade2Retail,
    t2Plan: d.t2Plan,
    daysInStock: d.daysInStock,
    estimatedGross: d.estimatedGross,
    actualGross: d.actualGross,
    fAndI: d.fAndI,
    points: d.points,
    holdback: d.holdback,
    doc: d.doc,
    financeSource: d.financeSource,
    financeSourceName: d.financeSourceName,
    financeReserve: d.financeReserve,
    openROs: d.openROs,
    salesperson: d.salesperson,
    salesperson2: d.salesperson2,
    salesManager: d.salesManager,
    fiRep: d.fiRep,
    customerLastName: d.customerLastName,
    csiBonus: d.csiBonus,
    accountingComment: d.accountingComment?.trim() || null, // empty/whitespace → null; server normalizes again
    comment: d.comment?.trim() || null, // empty/whitespace → null; server normalizes again
  };
}

/**
 * Merge a fresh server snapshot into the local rows without ever clobbering a
 * row the user is actively working on. A row is "protected" if it's dirty,
 * focused, or has a save in flight — those keep their local state, but we still
 * detect a same-row conflict (server version moved) or a remote delete and
 * surface a banner. Clean, unprotected rows are refreshed in place (reusing
 * their clientId so React doesn't remount them). Rows added by other users are
 * appended.
 */
function mergeRows(
  local: NutSheetRowDraft[],
  serverEntries: NutSheetEntry[],
  focusedClientId: string | null,
  savingClientIds: Set<string>,
): NutSheetRowDraft[] {
  const serverById = new Map(serverEntries.map((e) => [e.entryId, e]));
  const next: NutSheetRowDraft[] = [];

  for (const lr of local) {
    if (lr.entryId == null) {
      // Unsaved draft — the server doesn't know about it yet; never touch it.
      next.push(lr);
      continue;
    }
    const sr = serverById.get(lr.entryId);
    const isProtected =
      lr.isDirty || lr.clientId === focusedClientId || savingClientIds.has(lr.clientId);

    if (!sr) {
      // Row no longer exists on the server (deleted by someone else).
      if (isProtected) {
        next.push({ ...lr, deletedRemotely: true });
      } // else: drop it silently
      continue;
    }
    serverById.delete(lr.entryId);

    if (isProtected) {
      if (sr.version !== lr.version && !lr.deletedRemotely) {
        // Someone saved this same row while the user is editing it.
        next.push({
          ...lr,
          conflict: true,
          remoteEntry: sr,
          conflictBy: sr.updatedBy ?? null,
        });
      } else {
        next.push(lr);
      }
    } else {
      // Clean & idle: adopt the server copy in place.
      next.push(entryToDraft(sr, lr.clientId));
    }
  }

  // Rows newly created by other users.
  Array.from(serverById.values()).forEach((sr) => {
    next.push(entryToDraft(sr));
  });

  return next;
}

export const NutSheet: React.FC = () => {
  const { userRole, assignedLocationIds, assignedDepartmentIds } = useAuth();
  const isAuthorized = canAccessNutSheet(userRole, assignedDepartmentIds);
  // Only Accounting/Admin may write the per-row accounting comment; everyone
  // else sees it read-only (enforced server-side too — see save-entry).
  const canEditAccountingComment = userRole === 'Accounting' || userRole === 'Admin';

  // Pick an initial location: first assigned location for the user, else 1.
  const initialLocation = useMemo(() => {
    if (assignedLocationIds && assignedLocationIds.length > 0) {
      return assignedLocationIds[0];
    }
    return 1;
  }, [assignedLocationIds]);

  const [locationId, setLocationId] = useState<number>(initialLocation);
  const [date, setDate] = useState<string>(todayInEastern);

  const [rows, setRows] = useState<NutSheetRowDraft[]>([]);
  const [summary, setSummary] = useState<MtdSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingByIndex, setSavingByIndex] = useState<Record<number, boolean>>({});
  const [historyRow, setHistoryRow] = useState<
    { locationId: number; date: string; entryId: string } | null
  >(null);
  // Table row-visibility toggles. Operators usually focus on retail, so hiding
  // wholesale (USED "W") and/or swap (NEW "S") rows de-clutters heavy days. Each
  // choice persists per browser and defaults to showing.
  const [showWholesale, toggleWholesale] = usePersistedFlag(SHOW_WHOLESALE_KEY, true);
  // When on, only wholesale (USED "W") rows render. Overrides the Show Wholesale
  // and Show Swap toggles (disabled while it's active). Defaults off so users see all.
  const [showOnlyWholesale, toggleOnlyWholesale] = usePersistedFlag(SHOW_ONLY_WHOLESALE_KEY, false);
  // When on, only Value Center (USED "*VC") rows render. Mutually exclusive with
  // Show only Wholesale (each disables the other in the UI). Defaults off.
  const [showOnlyValueCenter, toggleOnlyValueCenter] = usePersistedFlag(SHOW_ONLY_VALUE_CENTER_KEY, false);
  // When on, Value Center (USED "*VC") rows are hidden — the inverse of Show only
  // Value Center (each disables the other in the UI). Defaults off.
  const [excludeValueCenter, toggleExcludeValueCenter] = usePersistedFlag(EXCLUDE_VALUE_CENTER_KEY, false);
  const [showSwap, toggleSwap] = usePersistedFlag(SHOW_SWAP_KEY, true);
  // Global F&I-reserve view toggle. Default off = include reserve (full F&I).
  // When on, each row's F&I cell shows fAndI − reserve (read-only) and the MTD
  // summary is recomputed net of reserve server-side (passed on the list fetch).
  const [excludeFiReserve, toggleExcludeFiReserve] = usePersistedFlag(EXCLUDE_FI_RESERVE_KEY, false);
  // Export to Excel modal (date-range picker).
  const [exportOpen, setExportOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  // Pending duplicate-deal-# warning: which row triggered it, the held lookup
  // result (auto-fill replayed only on confirm), and the existing matches.
  const [dupeWarning, setDupeWarning] = useState<{
    clientId: string;
    result: DealLookupResponse;
    matches: ExistingNutSheetMatch[];
  } | null>(null);

  // Which row currently has focus, and which rows have a save in flight — read
  // by the background poll's merge so it never clobbers an in-flight edit.
  // Refs (not state) so updating them doesn't re-render mid-keystroke.
  const focusedClientIdRef = useRef<string | null>(null);
  const savingClientIds = useRef<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    if (!isAuthorized) return;
    setLoading(true);
    setError(null);
    const res = await listNutSheetEntries(locationId, date, excludeFiReserve);
    if (res.success && res.data) {
      setRows(res.data.entries.map((e) => entryToDraft(e)));
      setSummary(res.data.mtdSummary);
    } else {
      setError(res.error || 'Failed to load nut-sheet entries');
      setRows([]);
      setSummary(EMPTY_SUMMARY);
    }
    setLoading(false);
  }, [locationId, date, isAuthorized, excludeFiReserve]);

  // Silent background refresh: pull the latest server rows and merge them in
  // without the loading spinner, so co-editors' saved changes appear within a
  // few seconds. Never overwrites a row the user is editing (see mergeRows).
  const pollData = useCallback(async () => {
    if (!isAuthorized) return;
    const res = await listNutSheetEntries(locationId, date, excludeFiReserve);
    if (res.success && res.data) {
      const server = res.data.entries;
      setRows((prev) =>
        mergeRows(prev, server, focusedClientIdRef.current, savingClientIds.current),
      );
      setSummary(res.data.mtdSummary);
    }
  }, [locationId, date, isAuthorized, excludeFiReserve]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll every 5s while the tab is visible; also refresh immediately on refocus.
  useEffect(() => {
    if (!isAuthorized) return;
    const tick = () => {
      if (document.visibilityState === 'visible') pollData();
    };
    const intervalId = window.setInterval(tick, 5000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [pollData, isAuthorized]);

  if (!isAuthorized) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded p-6 text-center">
          <h2 className="text-lg font-semibold text-yellow-900 mb-2">
            Nut Sheet Access Restricted
          </h2>
          <p className="text-yellow-800 text-sm">
            Available to Admin, CEO, and Executive users only.
          </p>
        </div>
      </div>
    );
  }

  const handleChangeRow = (index: number, patch: Partial<NutSheetRowDraft>) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch, isDirty: true };
      return next;
    });
  };

  const handleFocusRow = (clientId: string) => {
    focusedClientIdRef.current = clientId;
  };
  const handleBlurRow = () => {
    focusedClientIdRef.current = null;
  };

  // Shared save core, keyed by clientId so it's robust to a concurrent poll
  // reordering rows mid-save.
  const persistRow = async (row: NutSheetRowDraft) => {
    if (!row.type) return; // require a Type before persisting

    const startIdx = rows.findIndex((r) => r.clientId === row.clientId);
    savingClientIds.current.add(row.clientId);
    if (startIdx >= 0) setSavingByIndex((prev) => ({ ...prev, [startIdx]: true }));

    const res = await saveNutSheetEntry(draftToRequest(row));

    savingClientIds.current.delete(row.clientId);
    setSavingByIndex((prev) => {
      const next = { ...prev };
      if (startIdx >= 0) delete next[startIdx];
      return next;
    });

    if (res.success && res.data) {
      const saved = res.data;
      // Merge entryId + the new version + clear flags, but keep any keystrokes
      // typed while the save was in flight.
      setRows((prev) =>
        prev.map((r) =>
          r.clientId === row.clientId
            ? {
                ...r,
                entryId: saved.entryId,
                version: saved.version,
                isDirty: false,
                conflict: false,
                remoteEntry: null,
                conflictBy: null,
                deletedRemotely: false,
              }
            : r,
        ),
      );
      const refresh = await listNutSheetEntries(locationId, date, excludeFiReserve);
      if (refresh.success && refresh.data) setSummary(refresh.data.mtdSummary);
    } else if (res.conflict) {
      const serverEntry = (res.data as NutSheetEntry) ?? null;
      setRows((prev) =>
        prev.map((r) =>
          r.clientId === row.clientId
            ? {
                ...r,
                conflict: true,
                remoteEntry: serverEntry,
                conflictBy: serverEntry?.updatedBy ?? null,
              }
            : r,
        ),
      );
    } else {
      setError(res.error || 'Failed to save row');
    }
  };

  const handleCommitRow = (index: number) => {
    const row = rows[index];
    if (!row || !row.isDirty) return;
    void persistRow(row);
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, buildBlankDraft(locationId, date)]);
  };

  const handleShowHistory = (index: number) => {
    const row = rows[index];
    if (!row?.entryId) return; // unsaved rows have no history yet
    setHistoryRow({ locationId: row.locationId, date: row.date, entryId: row.entryId });
  };

  // The operator typed a deal # that already exists on this location's sheet
  // this month. Hold the lookup auto-fill and surface a Yes/No confirmation.
  const handleDuplicateDeal = (
    index: number,
    result: DealLookupResponse,
    matches: ExistingNutSheetMatch[],
  ) => {
    const row = rows[index];
    if (!row) return;
    setDupeWarning({ clientId: row.clientId, result, matches });
  };

  // Yes, re-enter: keep the deal # and apply the held DealerBuilt auto-fill.
  const handleConfirmDuplicate = () => {
    if (!dupeWarning) return;
    const { clientId, result } = dupeWarning;
    if (result.found) {
      setRows((prev) =>
        prev.map((r) =>
          r.clientId === clientId
            ? { ...r, ...dealAutofillPatch(result), isDirty: true }
            : r,
        ),
      );
    }
    setDupeWarning(null);
  };

  // No: clear the deal # so the deal isn't double-entered.
  const handleCancelDuplicate = () => {
    if (!dupeWarning) return;
    const { clientId } = dupeWarning;
    setRows((prev) =>
      prev.map((r) =>
        r.clientId === clientId ? { ...r, dealNumber: null, isDirty: true } : r,
      ),
    );
    setDupeWarning(null);
  };

  const handleDeleteRow = async (index: number) => {
    const row = rows[index];
    if (!row) return;
    if (!row.entryId) {
      // Unsaved draft — just drop it locally.
      setRows((prev) => prev.filter((r) => r.clientId !== row.clientId));
      return;
    }
    savingClientIds.current.add(row.clientId);
    setSavingByIndex((prev) => ({ ...prev, [index]: true }));
    const res = await deleteNutSheetEntry(
      row.locationId,
      row.date,
      row.entryId,
      row.version,
    );
    savingClientIds.current.delete(row.clientId);
    setSavingByIndex((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    if (res.success) {
      setRows((prev) => prev.filter((r) => r.clientId !== row.clientId));
      const refresh = await listNutSheetEntries(locationId, date, excludeFiReserve);
      if (refresh.success && refresh.data) {
        setSummary(refresh.data.mtdSummary);
      }
    } else if (res.conflict) {
      // The row changed since we loaded it — surface, don't silently delete.
      const serverEntry = (res.data as NutSheetEntry) ?? null;
      setRows((prev) =>
        prev.map((r) =>
          r.clientId === row.clientId
            ? {
                ...r,
                conflict: true,
                remoteEntry: serverEntry,
                conflictBy: serverEntry?.updatedBy ?? null,
              }
            : r,
        ),
      );
    } else {
      setError(res.error || 'Failed to delete row');
    }
  };

  // A newer server copy exists for a row the user was editing.
  const handleResolveConflict = (index: number, action: 'reload' | 'keepMine') => {
    const row = rows[index];
    if (!row) return;
    setRows((prev) =>
      prev.map((r) => {
        if (r.clientId !== row.clientId) return r;
        if (action === 'reload') {
          // Discard local edits, adopt the server copy.
          return r.remoteEntry
            ? entryToDraft(r.remoteEntry, r.clientId)
            : { ...r, conflict: false, remoteEntry: null, conflictBy: null };
        }
        // keepMine: rebase onto the latest server version so the next save
        // overwrites the server with the user's values.
        return {
          ...r,
          version: r.remoteEntry?.version ?? r.version,
          conflict: false,
          remoteEntry: null,
          conflictBy: null,
        };
      }),
    );
  };

  // The row was deleted by another user while this user was editing it.
  const handleResolveDeleted = (index: number, action: 'recreate' | 'discard') => {
    const row = rows[index];
    if (!row) return;
    if (action === 'discard') {
      setRows((prev) => prev.filter((r) => r.clientId !== row.clientId));
      return;
    }
    // recreate: drop the now-deleted entryId so the next save inserts a fresh
    // row carrying the user's edits.
    setRows((prev) =>
      prev.map((r) =>
        r.clientId === row.clientId
          ? { ...r, entryId: null, version: null, deletedRemotely: false, isDirty: true }
          : r,
      ),
    );
  };

  return (
    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <NutSheetHeader
        locationId={locationId}
        date={date}
        onLocationChange={setLocationId}
        onDateChange={setDate}
        onPrint={() => setPrintOpen(true)}
        onExport={() => setExportOpen(true)}
      />

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 bg-white rounded-lg shadow-md">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading…</span>
        </div>
      ) : (
        <>
          <NutSheetTable
            rows={rows}
            locationId={locationId}
            savingByIndex={savingByIndex}
            showWholesale={showWholesale}
            onToggleWholesale={toggleWholesale}
            showOnlyWholesale={showOnlyWholesale}
            onToggleOnlyWholesale={toggleOnlyWholesale}
            showOnlyValueCenter={showOnlyValueCenter}
            onToggleOnlyValueCenter={toggleOnlyValueCenter}
            excludeValueCenter={excludeValueCenter}
            onToggleExcludeValueCenter={toggleExcludeValueCenter}
            showSwap={showSwap}
            onToggleSwap={toggleSwap}
            excludeFiReserve={excludeFiReserve}
            onToggleExcludeFiReserve={toggleExcludeFiReserve}
            canEditAccountingComment={canEditAccountingComment}
            onChangeRow={handleChangeRow}
            onCommitRow={handleCommitRow}
            onDeleteRow={handleDeleteRow}
            onAddRow={handleAddRow}
            onFocusRow={handleFocusRow}
            onBlurRow={handleBlurRow}
            onResolveConflict={handleResolveConflict}
            onResolveDeleted={handleResolveDeleted}
            onShowHistory={handleShowHistory}
            onDuplicateDeal={handleDuplicateDeal}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NewVehicleSummary summary={summary} />
            <UsedVehicleSummary summary={summary} />
            <ValueCenterVehicleSummary summary={summary} />
            <TotalsSummary summary={summary} />
            <WholesaleVehicleSummary summary={summary} />
          </div>
        </>
      )}

      {historyRow && (
        <RowHistoryModal
          locationId={historyRow.locationId}
          date={historyRow.date}
          entryId={historyRow.entryId}
          onClose={() => setHistoryRow(null)}
        />
      )}

      {dupeWarning && (
        <DuplicateDealModal
          dealNumber={dupeWarning.result.dealNumber ?? ''}
          matches={dupeWarning.matches}
          onConfirm={handleConfirmDuplicate}
          onCancel={handleCancelDuplicate}
        />
      )}

      {exportOpen && (
        <NutSheetExportModal
          locationId={locationId}
          viewedDate={date}
          excludeFiReserve={excludeFiReserve}
          filters={{ showWholesale, showOnlyWholesale, showOnlyValueCenter, excludeValueCenter, showSwap }}
          onClose={() => setExportOpen(false)}
        />
      )}

      {printOpen && (
        <NutSheetPrintModal
          locationId={locationId}
          viewedDate={date}
          excludeFiReserve={excludeFiReserve}
          filters={{ showWholesale, showOnlyWholesale, showOnlyValueCenter, excludeValueCenter, showSwap }}
          onClose={() => setPrintOpen(false)}
        />
      )}
    </div>
  );
};
